import { useMemo, useRef, useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon';
import LinkIcon from '@/components/Icons/LinkIcon';
import Loading from '@/components/Loading';
import { links } from '@/localization';
import ListOptions from '@/sections/Workspace/Filter/ListOptions';
import Search from '@/sections/Workspace/Filter/Search';
import ViewModes from '@/sections/Workspace/Filter/ViewModes';
import { ViewMode } from '@/sections/Workspace/types';
import Fuse from 'fuse.js';

import { BASE_FUSE_SEARCH_OPTIONS, mapFilterToTitle, mapSortByToTitle } from '../constants';
import { useFilterQuery, useTableContainerHeight, useViewMode } from '../hooks';
import EmptyState from '../sections/EmptyStates';
import { IconContainer } from '../styled';
import { Filter, SortBy, WorkspaceSections } from '../types';
import { filterChainsByStatus } from '../utils';
import ChainsEmptyState from './EmptyState';
import Grid from './Grid';
import Table from './Table';
import { WorkspaceChainFragment } from './__generated__/fragments.generated';
import { useChains } from './hooks';
import { getChainsWithDeployments, sortChains } from './utils';

const FUSE_SEARCH_OPTIONS = {
  ...BASE_FUSE_SEARCH_OPTIONS,
  keys: ['name', 'id'],
};

function Chains() {
  const { chains, loading } = useChains(true);
  const { viewMode, setViewMode } = useViewMode(WorkspaceSections.Chains);
  const { filterQuery, setFilterQuery } = useFilterQuery(WorkspaceSections.Chains);
  const [selectedFilter, setSelectedFilter] = useState<Filter>(Filter.All);
  const [selectedSortBy, setSelectedSortBy] = useState<SortBy>(SortBy.PRODUCTION);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerHeight = useTableContainerHeight(containerRef, Number(loading));

  const sortedChains = useMemo(() => {
    const chainsWithDeployments = getChainsWithDeployments(chains);

    return sortChains(chainsWithDeployments, selectedSortBy);
  }, [chains, selectedSortBy]);

  const filteredChains = useMemo(() => {
    const filteredByStatus = filterChainsByStatus(sortedChains, selectedFilter);

    if (!filterQuery) {
      return filteredByStatus;
    }

    const fuse = new Fuse<WorkspaceChainFragment>(filteredByStatus, FUSE_SEARCH_OPTIONS);
    return fuse.search(filterQuery).map(({ item }) => item);
  }, [filterQuery, sortedChains, selectedFilter]);

  function getContent() {
    if (loading) {
      return <Loading />;
    }

    if (isEmpty(chains)) {
      return <ChainsEmptyState />;
    }

    if (isEmpty(filteredChains)) {
      return <EmptyState>No Chains found</EmptyState>;
    }

    return viewMode === ViewMode.Grid ? (
      <Grid chains={filteredChains} />
    ) : (
      <Table chains={filteredChains} height={tableContainerHeight} />
    );
  }

  return (
    <Stack height="100%">
      <Stack gap={3} px={4} pt={4} pb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer>
              <LinkIcon />
            </IconContainer>
            <Typography variant="h2">Chains</Typography>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ExternalLinkIcon />}
            href={links.basetenDocsChainsOverview}
            target="_blank"
          >
            Deploy a Chain
          </Button>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Search query={filterQuery} setQuery={setFilterQuery} />
          <ListOptions<SortBy>
            title="Sort"
            options={Object.values(SortBy)}
            selectedOption={selectedSortBy}
            onOptionSelect={setSelectedSortBy}
            mapOptionToTitle={(option) => mapSortByToTitle[option]}
          />
          <ListOptions<Filter>
            title="Status"
            options={Object.values(Filter)}
            selectedOption={selectedFilter}
            onOptionSelect={setSelectedFilter}
            mapOptionToTitle={(option) => mapFilterToTitle[option]}
          />
          <ViewModes viewMode={viewMode} setViewMode={setViewMode} />
        </Stack>
      </Stack>
      <Stack flex={1} ref={containerRef} overflow="hidden">
        {getContent()}
      </Stack>
    </Stack>
  );
}

export default Chains;
